import { shape, string } from 'prop-types';
import dynamic from 'next/dynamic';
import classnames from 'classnames';
import React, { useRef, useEffect, useState, Fragment } from 'react';
import throttle from 'lodash.throttle';
import PageActions from 'components/PageActions';
import RecipePrintButton from 'components/RecipePrintButton';
import RecipeRating from 'components/RecipeRating';
import SiteSquiggle from 'constants/logos/SiteSquiggle';
import RecipeRatingsDataProvider from 'components/RecipeRatingsDataProvider';
import buildRecipeStructuredData from 'components/Recipe/RecipeStructuredDataFormatter';
import { convertTimeToComponents } from 'components/Recipe/helpers';
import styles from './styles';

const SaveToRecipeBoxActions = dynamic(
  () => import('components/SaveToRecipeBoxActions'),
  {
    ssr: false
  }
);

function RecipeFirstStickyNav(props) {
  const { post, recipe } = props;

  const pageActionProps = {
    resourceId: post.resourceId,
    postImage: post.pinterestBoardImage,
    postUrl: post.canonical,
    postTitle: post.title,
    disable: [''],
    printRecipeButton: (
      <RecipePrintButton {...recipe} placement="recipe-first" />
    ),
    isAmp: false
  };

  const [navPosition, setNavPosition] = useState(null);
  const navSection = useRef(null);

  useEffect(() => {
    function updateNavPosition() {
      setNavPosition(navSection.current.getBoundingClientRect().y);
    }

    const throttledUpdate = throttle(updateNavPosition, 1000);

    document.addEventListener('scroll', throttledUpdate);

    return function cleanup() {
      document.removeEventListener('scroll', throttledUpdate);
    };
  }, [navPosition]);

  const shouldStickToTop = navPosition < 0;

  const convertedPrepTime = recipe.prepTime
    ? convertTimeToComponents(recipe.prepTime)
    : undefined;

  return (
    <div
      ref={navSection}
      id="RecipeFirstStickyNav"
      className="RecipeFirstStickyNav"
    >
      <div
        className={classnames('RecipeFirstStickyNav__body', {
          'RecipeFirstStickyNav__body--sticky': shouldStickToTop
        })}
      >
        <div className="RecipeFirstStickyNav__container">
          <div className="RecipeFirstStickyNav__intro">
            {/* Small mobile only logo */}
            <a className="RecipeFirstStickyNav__smallLogo" href="#">
              <SiteSquiggle />
            </a>

            {/* Recipe Title */}
            <a href="#">
              <h2
                className="RecipeFirstStickyNav__postTitle"
                dangerouslySetInnerHTML={{ __html: recipe.title }}
              />
            </a>
          </div>

          <div className="RecipeFirstStickyNav__infoContainer">
            {/* Save to Recipe Box Desktop */}
            <div className="RecipeFirstStickyNav__saveToRecipeBoxActions">
              <SaveToRecipeBoxActions
                recipeId={recipe.recipeId}
                postId={post.resourceId}
                postUrl={post.canonical}
                gtmAttrs={{ placement: 'Recipe First Sticky Nav' }}
                isSticky={false}
              />
            </div>

            {/* Rating */}
            <RecipeRatingsDataProvider recipeId={recipe.recipeId}>
              {({
                ratingsData,
                rateRecipe,
                rateRecipeIsLoading,
                fetchUserRating
              }) => (
                <div className="RecipeFirstStickyNav__rating">
                  {!!recipe.title && !!recipe.imageUrl && (
                    <script
                      className="json-ld-recipe"
                      type="application/ld+json"
                      dangerouslySetInnerHTML={{
                        __html: JSON.stringify(
                          buildRecipeStructuredData({ ratingsData, ...recipe })
                        )
                      }}
                    />
                  )}
                  <RecipeRating
                    ratingsData={ratingsData}
                    rateRecipe={rateRecipe}
                    rateRecipeIsLoading={rateRecipeIsLoading}
                    recipeId={parseInt(recipe.recipeId, 10)}
                    fetchUserRating={fetchUserRating}
                  />
                </div>
              )}
            </RecipeRatingsDataProvider>

            {/* Recipe Info */}
            <div className="RecipeFirstStickyNav__info">
              {(recipe.makes || recipe.serves) && (
                <div className="RecipeFirstStickyNav__info__yeild">
                  {recipe.serves ? `${recipe.serves}` : ''}
                  {recipe.serves && recipe.makes && `, `}
                  {recipe.makes ? `${recipe.makes}` : ''}
                  <div className="RecipeFirstStickyNav__yieldLabel">Yield</div>
                </div>
              )}
              {((recipe.cookTime && recipe.cookTime.min) ||
                (recipe.prepTime && recipe.prepTime.min)) && (
                <Fragment>
                  <div className="RecipeFirstStickyNav__time">
                    {convertedPrepTime && (
                      <div className="RecipeFirstStickyNav__timeEntry">
                        {convertedPrepTime.humanString
                          .split('minutes')
                          .join('min')}
                        <div className="RecipeFirstStickyNav__timeLabel">
                          Prep
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="RecipeFirstStickyNav__time">
                    {convertTimeToComponents(recipe.cookTime) && (
                      <p className="RecipeFirstStickyNav__timeEntry">
                        {convertTimeToComponents(recipe.cookTime)
                          .humanString.split('minutes')
                          .join('min')}
                        <div className="RecipeFirstStickyNav__timeLabel">
                          Cook
                        </div>
                      </p>
                    )}
                  </div>
                </Fragment>
              )}
            </div>

            {/* Hyperlinks */}
            <div className="RecipeFirstStickyNav__mobileLinks">
              <a
                className="RecipeFirstStickyNav__jumpToRecipe"
                href="#RecipeFirstStickyNav"
              >
                Recipe
              </a>
              <a
                className="RecipeFirstStickyNav__jumpToStory"
                href="#recipe-story"
              >
                Story
              </a>
              <a
                className="RecipeFirstStickyNav__jumpToComments Post__jumpToComments SitePostActions__comment-count-action"
                href={`#comments-${post.resourceId}`}
                data-gtm-placement="recipe-first"
                data-gtm-social-network="community"
              >
                Conversation
              </a>
            </div>
          </div>

          <div className="RecipeFirstStickyNav__actions">
            {/* Save to Recipe Box Mobile */}
            <div className="RecipeFirstStickyNav__saveToRecipeBoxActionsMobile">
              <SaveToRecipeBoxActions
                recipeId={recipe.recipeId}
                postId={post.resourceId}
                postUrl={post.canonical}
                gtmAttrs={{ placement: 'Recipe First Sticky Nav' }}
                isSticky={false}
              />
            </div>

            {/* Share Actions */}
            <div className="RecipeFirstStickyNav__shareActions">
              <PageActions placement="post-bottom" {...pageActionProps} />
            </div>
          </div>
        </div>
        <style jsx>{styles}</style>
      </div>
    </div>
  );
}

RecipeFirstStickyNav.defaultProps = {};

RecipeFirstStickyNav.propTypes = {
  recipe: shape({
    recipeId: string.isRequired
  }).isRequired,
  post: shape({
    title: string.isRequired
  }).isRequired
};

export default RecipeFirstStickyNav;
